import { Architecture, Circle, GpsFixed, RadioButtonUnchecked, ViewInAr } from '@mui/icons-material';
import { Box, ClickAwayListener, Modal, Radio } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { EMultiToggleElements } from '../../../../@types/Enums';
import {
  IconAttachmentPin,
  IconDoorLength,
  IconInnerWallLength,
  IconLineal,
  IconMeasure,
  IconMoreInfo,
  IconOuterWallLength,
  IconPassage,
  IconProductDetails,
  IconRadiator,
  IconRoomDetails,
  IconWindowLength
} from '../../../../assets/icons';
import { useMultiSelectToggleContext } from '../../../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../../../provider/ToolbarProvider';
import { immersightBlue, primaryIconInactive, secondaryIconHover } from '../../../../utils/colors';
import { isMUIIcon } from '../../../../utils/iconUtils';
import ImsIcon from '../../ImsIcon';

const iconStyle = {
  width: '2.25rem',
  height: '2.25rem',
  margin: '.1rem .5rem',
  transition: '.2s ease-in-out',
};

interface VisibilityModalProps {
  open: boolean;
  onClose: () => void;
  sizes: any;
  buttonPosition?: DOMRect;
}

const VisibilityModal: React.FC<VisibilityModalProps> = ({ open, onClose, sizes, buttonPosition }) => {
  const intl = useIntl();
  const {
    selectedItems,
    saveSelectedItems,
    showWallInnerLength,
    setShowWallInnerLength,
    showWallOuterLength,
    setShowWallOuterLength,
    showWindowLength,
    setShowWindowLength,
    showDoorLength,
    setShowDoorLength,
    showPassageLength,
    setShowPassageLength,
    showRadiatorLength,
    setShowRadiatorLength,
    showRoomDetails,
    setShowRoomDetails,
    showCameraPositionSprites,
    setShowCameraPositionSprites,
    showAngles,
    setShowAngles,
    showRoomComponentDetails,
    setShowRoomComponentDetails,
    showElementDetails,
    setShowElementDetails,
    showMeasurements,
    setShowMeasurements,
    showGeneralInfo,
    setShowGeneralInfo,
    showElementDistances,
    setShowElementDistances,
    showHotspots,
    setShowHotspots,
  } = useMultiSelectToggleContext();
  const { setCurrentlyActiveSubMenu } = useToolbarContext();

  // Define the list of multi-select items
  const multiSelectItems = [
    {
      icon: IconInnerWallLength,
      text: intl.formatMessage({ id: 'MultiSelectToggle.wallInner' }),
      id: EMultiToggleElements.WALL_INNER_LENGTH,
    },
    {
      icon: IconOuterWallLength,
      text: intl.formatMessage({ id: 'MultiSelectToggle.wallOuter' }),
      id: EMultiToggleElements.WALL_OUTER_LENGTH,
    },
    {
      icon: IconLineal,
      text: intl.formatMessage({ id: 'MultiSelectToggle.inWallDistances' }),
      id: EMultiToggleElements.ELEMENT_DISTANCES,
    },
    {
      icon: IconWindowLength,
      text: intl.formatMessage({ id: 'MultiSelectToggle.windowLength' }),
      id: EMultiToggleElements.WINDOW_LENGTH,
    },
    {
      icon: IconDoorLength,
      text: intl.formatMessage({ id: 'MultiSelectToggle.doorLength' }),
      id: EMultiToggleElements.DOOR_LENGTH,
    },
    {
      icon: IconPassage,
      text: intl.formatMessage({ id: 'MultiSelectToggle.passageLength' }),
      id: EMultiToggleElements.PASSAGE_LENGTH,
    },
    {
      icon: IconRadiator,
      text: intl.formatMessage({ id: 'MultiSelectToggle.radiatorLength' }),
      id: EMultiToggleElements.RADIATOR_LENGTH,
    },
    {
      icon: IconRoomDetails,
      text: intl.formatMessage({ id: 'MultiSelectToggle.roomDetails' }),
      id: EMultiToggleElements.ROOM_DETAILS,
    },
    {
      icon: Architecture,
      text: intl.formatMessage({ id: 'MultiSelectToggle.angles' }),
      id: EMultiToggleElements.ANGLES,
    },
    {
      icon: IconProductDetails,
      text: intl.formatMessage({ id: 'MultiSelectToggle.roomComponentsDetails' }),
      id: EMultiToggleElements.ROOM_COMPONENT_DETAILS,
    },
    {
      icon: ViewInAr,
      text: intl.formatMessage({ id: 'MultiSelectToggle.elementDetails' }),
      id: EMultiToggleElements.ELEMENT_DETAILS,
    },
    {
      icon: IconAttachmentPin,
      text: intl.formatMessage({ id: 'MultiSelectToggle.hotspot' }),
      id: EMultiToggleElements.HOTSPOTS,
    },
    {
      icon: IconMeasure,
      text: intl.formatMessage({ id: 'MultiSelectToggle.measurements' }),
      id: EMultiToggleElements.MEASUREMENTS,
    },
    {
      icon: IconMoreInfo,
      text: intl.formatMessage({ id: 'MultiSelectToggle.generalInfo' }),
      id: EMultiToggleElements.GENERAL_INFO,
    },
    {
      icon: GpsFixed,
      text: intl.formatMessage({ id: 'MultiSelectToggle.cameraPosition' }),
      id: EMultiToggleElements.CAMERA_POSITION_SPRITE,
    },
  ];

  // Handler when an item is clicked
  const handleItemClicked = (itemId: number): void => {
    if (selectedItems.includes(itemId)) {
      saveSelectedItems(selectedItems.filter((item) => item !== itemId));
    } else {
      saveSelectedItems([...selectedItems, itemId]);
    }

    switch (itemId) {
      case EMultiToggleElements.WALL_INNER_LENGTH:
        setShowWallInnerLength(!showWallInnerLength);
        break;
      case EMultiToggleElements.WALL_OUTER_LENGTH:
        setShowWallOuterLength(!showWallOuterLength);
        break;
      case EMultiToggleElements.WINDOW_LENGTH:
        setShowWindowLength(!showWindowLength);
        break;
      case EMultiToggleElements.DOOR_LENGTH:
        setShowDoorLength(!showDoorLength);
        break;
      case EMultiToggleElements.PASSAGE_LENGTH:
        setShowPassageLength(!showPassageLength);
        break;
      case EMultiToggleElements.RADIATOR_LENGTH:
        setShowRadiatorLength(!showRadiatorLength);
        break;
      case EMultiToggleElements.ROOM_DETAILS:
        setShowRoomDetails(!showRoomDetails);
        break;
      case EMultiToggleElements.ANGLES:
        setShowAngles(!showAngles);
        break;
      case EMultiToggleElements.ROOM_COMPONENT_DETAILS:
        setShowRoomComponentDetails(!showRoomComponentDetails);
        break;
      case EMultiToggleElements.ELEMENT_DETAILS:
        setShowElementDetails(!showElementDetails);
        break;
      case EMultiToggleElements.HOTSPOTS:
        setShowHotspots(!showHotspots);
        break;
      case EMultiToggleElements.MEASUREMENTS:
        setShowMeasurements(!showMeasurements);
        break;
      case EMultiToggleElements.GENERAL_INFO:
        setShowGeneralInfo(!showGeneralInfo);
        break;
      case EMultiToggleElements.CAMERA_POSITION_SPRITE:
        setShowCameraPositionSprites(!showCameraPositionSprites);
        break;
      case EMultiToggleElements.ELEMENT_DISTANCES:
        setShowElementDistances(!showElementDistances);
        break;
      default:
        break;
    }
  };

  // Render each item similar to the behavior items
  const renderSelectionItem = (item: { icon: any; text: string; id: number }, index: number): JSX.Element => {
    const ItemIcon = item.icon;
    return (
      <Box
        component="div"
        key={`listItem-${index}`}
        onClick={() => handleItemClicked(item.id)}
        sx={{
          display: 'grid',
          gridTemplateRows: '1fr',
          gridTemplateColumns: '18% 69% 5% 8%',
          alignItems: 'center',
          padding: '0.1rem 0.7rem',
          fontSize: '1rem',
          cursor: 'pointer',
          borderRadius: 0,
          color: selectedItems.includes(item.id) ? immersightBlue : primaryIconInactive,
          width: 'min(95vw, 20rem)',
          userSelect: 'none',
          '&:hover': {
            bgcolor: secondaryIconHover,
          },
        }}
      >
        {isMUIIcon(ItemIcon) ? <ItemIcon style={iconStyle} /> : <ImsIcon Icon={ItemIcon} style={iconStyle} />}
        {item.text}
        <Radio
          checked={selectedItems.includes(item.id)}
          icon={<RadioButtonUnchecked />}
          checkedIcon={<Circle />}
          sx={{
            color: primaryIconInactive,
            '&.Mui-checked': {
              color: immersightBlue,
              strokeWidth: 2,
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1002,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 48}px` : '50%',
        width: 'fit-content',
        transform: 'translateY(-80%)',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Title Section with Dividing Line */}
            <Box
              sx={{
                padding: '0.5rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.5rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.visibility' })}
              </Box>
            </Box>
            {/* Items List */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {multiSelectItems.map((item, index) => renderSelectionItem(item, index))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default VisibilityModal;
